// EPIC-P05-07: Badge Admin Page
// Main page for badge management

import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { SectionHero } from '@/components/admin/primitives'
import { Button } from '@/components/ui/button'
import { Plus, Loader2, AlertCircle } from 'lucide-react'
import { BadgeFilterBar, BadgeFilters } from '@/components/admin/badges/BadgeFilterBar'
import { BadgeTable } from '@/components/admin/badges/BadgeTable'
import { getBadgesList, BadgeListItem } from '@/lib/api/admin/badges'
import { isFeatureEnabled } from '@/lib/featureFlags'
import { BadgeDetailDrawer } from '@/components/admin/badges/BadgeDetailDrawer'
import { BadgeForm } from '@/components/admin/badges/BadgeForm'
import { useAdminAccess } from '@/hooks/useAdminAccess'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

export default function BadgeAdminPage() {
  const { isPlatformAdmin, isLoading: adminLoading } = useAdminAccess()
  const badgesAdminEnabled = isFeatureEnabled('badges_admin')
  const [selectedBadge, setSelectedBadge] = useState<BadgeListItem | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [page, setPage] = useState(1)
  const [errorDetails, setErrorDetails] = useState<string[]>([])
  const [filters, setFilters] = useState<BadgeFilters>({
    search: '',
    category: '',
    rarity: '',
    is_active: '',
    sort_by: 'updated_at',
    sort_order: 'desc',
  })

  // Capture all console errors to display in UI
  useEffect(() => {
    const originalError = console.error
    const originalWarn = console.warn
    const errors: string[] = []

    console.error = (...args: unknown[]) => {
      errors.push(args.map(a => typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a)).join(' '))
      setErrorDetails([...errors])
      originalError.apply(console, args)
    }

    console.warn = (...args: unknown[]) => {
      errors.push('WARN: ' + args.map(a => typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a)).join(' '))
      setErrorDetails([...errors])
      originalWarn.apply(console, args)
    }

    return () => {
      console.error = originalError
      console.warn = originalWarn
    }
  }, [])

  const canQuery = badgesAdminEnabled && !adminLoading && !!isPlatformAdmin

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-badges', page, filters],
    enabled: canQuery,
    queryFn: async () => {
      try {
        console.log('[BadgeAdminPage] Starting query function');
        const result = await getBadgesList({ ...filters, page, limit: 50 });
        console.log('[BadgeAdminPage] Query function completed successfully');
        return result;
      } catch (err) {
        console.error('[BadgeAdminPage] Query function threw error');
        console.error('[BadgeAdminPage] Error:', err);
        console.error('[BadgeAdminPage] Error type:', typeof err);
        if (err instanceof Error) {
          console.error('[BadgeAdminPage] Error message:', err.message);
          console.error('[BadgeAdminPage] Error stack:', err.stack);
        }
        throw err;
      }
    },
    staleTime: 30000, // 30 seconds
    retry: 2,
    retryOnMount: true,
  })

  // Check feature flag
  if (!badgesAdminEnabled) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Feature Disabled</h1>
          <p className="text-muted-foreground">
            Badge management is currently disabled. Enable it via feature flag.
          </p>
        </div>
      </div>
    )
  }

  // Check admin access
  if (adminLoading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!isPlatformAdmin) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
          <p className="text-muted-foreground">
            You need platform administrator privileges to access this page.
          </p>
        </div>
      </div>
    )
  }

  const handleBadgeClick = (badge: BadgeListItem) => {
    setSelectedBadge(badge)
  }

  const handleCloseDrawer = () => {
    setSelectedBadge(null)
    refetch()
  }

  const handleCreateSuccess = () => {
    setShowCreateForm(false)
    refetch()
  }

  return (
    <div className="min-h-screen bg-surface pb-20">
      <div className="max-w-admin mx-auto px-admin-content-padding py-8">
        {/* Hero Section */}
        <SectionHero
          title="Badge Management"
          description="Create and manage platform badges, criteria, and assignments"
          gradient="primary"
          actions={
            <Button onClick={() => setShowCreateForm(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Badge
            </Button>
          }
        />

        {/* Filters */}
        <div className="mt-6">
          <BadgeFilterBar
            filters={filters}
            onFiltersChange={(newFilters) => {
              setFilters({ ...filters, ...newFilters })
              setPage(1) // Reset to first page on filter change
            }}
          />
        </div>

        {/* Error Display */}
        {errorDetails.length > 0 && (
          <div className="mt-6">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Console Errors Detected ({errorDetails.length})</AlertTitle>
              <AlertDescription>
                <details className="mt-2">
                  <summary className="cursor-pointer font-semibold">View errors</summary>
                  <div className="mt-2 space-y-1 max-h-60 overflow-auto">
                    {errorDetails.map((err, idx) => (
                      <pre key={idx} className="text-xs p-2 bg-muted rounded overflow-auto">
                        {err}
                      </pre>
                    ))}
                  </div>
                </details>
              </AlertDescription>
            </Alert>
          </div>
        )}

        {/* Badge Table */}
        <div className="mt-6">
          {error ? (
            <div className="bg-admin-warning/10 border border-admin-warning/20 rounded-lg p-4">
              <div className="text-center">
                <p className="text-sm font-semibold text-foreground mb-2">Failed to load badges</p>
                <p className="text-sm text-muted-foreground mb-2">
                  {error instanceof Error ? error.message : String(error)}
                </p>
                {error instanceof Error && error.stack && (
                  <details className="text-xs text-muted-foreground mt-2 text-left">
                    <summary className="cursor-pointer">Stack trace</summary>
                    <pre className="mt-2 p-2 bg-muted rounded overflow-auto">{error.stack}</pre>
                  </details>
                )}
                <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4">
                  Retry
                </Button>
              </div>
            </div>
          ) : (
            <BadgeTable
              badges={data?.badges || []}
              onBadgeClick={handleBadgeClick}
              isLoading={isLoading}
            />
          )}
        </div>

        {/* Pagination */}
        {data?.pagination && data.pagination.total_pages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {((page - 1) * 50) + 1} to {Math.min(page * 50, data.pagination.total)} of {data.pagination.total} badges
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <div className="text-sm text-muted-foreground">
                Page {page} of {data.pagination.total_pages}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(data.pagination.total_pages, p + 1))}
                disabled={page >= data.pagination.total_pages}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Detail Drawer */}
        {selectedBadge && (
          <BadgeDetailDrawer
            badgeId={selectedBadge.id}
            open={!!selectedBadge}
            onClose={handleCloseDrawer}
          />
        )}

        {/* Create Form */}
        {showCreateForm && (
          <BadgeForm
            open={showCreateForm}
            onClose={() => setShowCreateForm(false)}
            onSuccess={handleCreateSuccess}
          />
        )}
      </div>
    </div>
  )
}

